import React, { useState, useRef } from 'react';
import { MessageSquare, X, Upload, Mail, Phone, Trash2, FileText, Link } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const SignQuotesTab = ({ 
  isDark, 
  authFetch, 
  showToast, 
  quotes, 
  setQuotes,
  generatedQuotes,
  onPrefillFromQuoteRequest,
  onSetActiveTab
}) => {
  // Quote conversation state
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteMessages, setQuoteMessages] = useState([]);
  const [newResponse, setNewResponse] = useState({ message: '', price_quote: '', attachments: [] });
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingResponse, setSendingResponse] = useState(false);
  const [uploadingResponseFile, setUploadingResponseFile] = useState(false);
  const responseFileInputRef = useRef(null);

  // Filter to only show sign quotes (exclude business and brand boost)
  const signQuotes = quotes.filter(q => 
    !q.sign_type?.startsWith('Business Studio') && 
    !q.sign_type?.startsWith('Brand Boost')
  );

  // Quote conversation functions
  const openQuoteConversation = async (quote) => {
    setSelectedQuote(quote);
    setLoadingMessages(true);
    setNewResponse({ message: '', price_quote: '', attachments: [] });
    try {
      const res = await authFetch(`${API_URL}/api/admin/quotes/${quote.id}/messages`);
      if (res.ok) {
        const messages = await res.json();
        setQuoteMessages(messages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const closeQuoteConversation = () => {
    setSelectedQuote(null);
    setQuoteMessages([]);
    setNewResponse({ message: '', price_quote: '', attachments: [] });
  };

  const handleResponseFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploadingResponseFile(true);
    const newFiles = [];
    
    for (const file of files) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) continue;
      if (file.size > 10 * 1024 * 1024) continue;
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await authFetch(`${API_URL}/api/admin/upload`, {
          method: 'POST',
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          newFiles.push({
            url: data.url,
            name: file.name,
            type: file.type
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
    
    if (newFiles.length > 0) {
      setNewResponse(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles]
      }));
    }
    setUploadingResponseFile(false);
  };

  const removeResponseAttachment = (index) => {
    setNewResponse(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const sendQuoteResponse = async () => {
    if (!selectedQuote || (!newResponse.message.trim() && newResponse.attachments.length === 0)) return;
    
    setSendingResponse(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/quotes/${selectedQuote.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: newResponse.message,
          price_quote: newResponse.price_quote ? parseFloat(newResponse.price_quote) : null,
          attachments: newResponse.attachments
        })
      });
      
      if (res.ok) {
        const newMessage = await res.json();
        setQuoteMessages(prev => [...prev, newMessage]);
        setNewResponse({ message: '', price_quote: '', attachments: [] });
        updateQuoteStatus(selectedQuote.id, 'responded');
        showToast('Response sent successfully');
      } else {
        showToast('Failed to send response', 'error');
      }
    } catch (error) {
      console.error('Error sending response:', error);
      showToast('Error sending response', 'error');
    }
    setSendingResponse(false);
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}/status?status=${status}`, {
        method: 'PUT'
      });
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status } : q));
      showToast('Quote status updated');
    } catch (error) {
      showToast('Error updating quote', 'error');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}`, {
        method: 'DELETE'
      });
      setQuotes(quotes.filter(q => q.id !== quoteId));
      showToast('Quote deleted');
    } catch (error) {
      showToast('Error deleting quote', 'error');
    }
  };

  const copyMagicLink = (quote) => {
    const link = `${window.location.origin}/quote/${quote.access_token}`;
    navigator.clipboard.writeText(link);
    showToast('Link copied to clipboard');
  };

  const saveAdminNotes = async (quoteId, notes) => {
    try {
      await authFetch(`${API_URL}/api/admin/quotes/${quoteId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes })
      });
      showToast('Notes saved');
    } catch (err) {
      console.error('Failed to save notes:', err);
    }
  };

  return (
    <div className="space-y-4" data-testid="sign-quotes-tab">
      {/* Quote Conversation Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeQuoteConversation}
          />
          <div 
            className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col"
            style={{
              backgroundColor: isDark ? '#111111' : '#ffffff',
              border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`,
              boxShadow: isDark ? '0 20px 60px rgba(0, 255, 136, 0.3)' : '0 20px 60px rgba(16, 185, 129, 0.2)'
            }}
          >
            {/* Modal Header */}
            <div className="p-4 border-b" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                    Quote Conversation - {selectedQuote.name}
                  </h2>
                  <p className="text-sm" style={{ color: isDark ? '#666' : '#64748b' }}>
                    {selectedQuote.email} - {selectedQuote.sign_type}
                  </p>
                </div>
                <button 
                  onClick={closeQuoteConversation}
                  className="p-2 rounded-lg hover:bg-white/10"
                  style={{ color: isDark ? '#666' : '#94a3b8' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Magic Link */}
              <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(14, 165, 233, 0.1)' }}>
                <p className="text-xs mb-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                  Customer Portal Link (share with customer to view responses):
                </p>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    readOnly 
                    value={`${window.location.origin}/quote/${selectedQuote.access_token || 'N/A'}`}
                    className="flex-1 p-2 rounded text-xs"
                    style={{ backgroundColor: isDark ? '#1a1a1a' : '#f1f5f9', color: isDark ? '#888' : '#64748b' }}
                  />
                  <button
                    onClick={() => copyMagicLink(selectedQuote)}
                    className="px-3 py-2 rounded text-xs font-medium"
                    style={{ backgroundColor: isDark ? '#00ffff' : '#0ea5e9', color: isDark ? '#000' : '#fff' }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '40vh' }}>
              {loadingMessages ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto" />
                </div>
              ) : quoteMessages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2" style={{ color: isDark ? '#333' : '#e2e8f0' }} />
                  <p style={{ color: isDark ? '#666' : '#94a3b8' }}>No messages yet. Send a response below.</p>
                </div>
              ) : (
                quoteMessages.map((msg) => (
                  <div 
                    key={msg.id}
                    className={`p-4 rounded-lg ${msg.sender_type === 'admin' ? 'ml-8' : 'mr-8'}`}
                    style={{
                      backgroundColor: msg.sender_type === 'admin' 
                        ? (isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)')
                        : (isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)'),
                      border: `1px solid ${msg.sender_type === 'admin' ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#ff00ff' : '#d946ef')}`
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium" style={{ color: msg.sender_type === 'admin' ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#ff00ff' : '#d946ef') }}>
                        {msg.sender_type === 'admin' ? 'You' : selectedQuote.name}
                      </span>
                      <span className="text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p style={{ color: isDark ? '#ccc' : '#334155' }}>{msg.message}</p>
                    {msg.price_quote && (
                      <div className="mt-2 p-2 rounded" style={{ backgroundColor: isDark ? 'rgba(0, 255, 136, 0.2)' : 'rgba(16, 185, 129, 0.2)' }}>
                        <span className="font-semibold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                          Quoted Price: ¥{msg.price_quote.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={att.url.startsWith('http') ? att.url : `${API_URL}${att.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 rounded text-xs flex items-center gap-1"
                            style={{ backgroundColor: isDark ? '#333' : '#e2e8f0', color: isDark ? '#ccc' : '#334155' }}
                          >
                            {att.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {/* Response Form */}
            <div className="p-4 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
              {/* Attachments Preview */}
              {newResponse.attachments.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {newResponse.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-2 py-1 rounded text-xs" style={{ backgroundColor: isDark ? '#333' : '#e2e8f0' }}>
                      {att.name}
                      <button onClick={() => removeResponseAttachment(idx)} className="text-red-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <textarea
                    value={newResponse.message}
                    onChange={(e) => setNewResponse(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Type your response to the customer..."
                    className="w-full p-3 rounded-lg text-sm resize-none"
                    rows={3}
                    style={{
                      backgroundColor: isDark ? '#1a1a1a' : '#f1f5f9',
                      color: isDark ? '#fff' : '#0f172a',
                      border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                    }}
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={newResponse.price_quote}
                      onChange={(e) => setNewResponse(prev => ({ ...prev, price_quote: e.target.value }))}
                      placeholder="Quote price (¥)"
                      className="w-40 p-2 rounded text-sm"
                      style={{
                        backgroundColor: isDark ? '#1a1a1a' : '#f1f5f9',
                        color: isDark ? '#fff' : '#0f172a',
                        border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                      }}
                    />
                    <button
                      onClick={() => responseFileInputRef.current?.click()}
                      disabled={uploadingResponseFile}
                      className="px-3 py-2 rounded text-sm flex items-center gap-1"
                      style={{
                        backgroundColor: isDark ? '#333' : '#e2e8f0',
                        color: isDark ? '#ccc' : '#334155'
                      }}
                    >
                      {uploadingResponseFile ? (
                        <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Attach Files
                    </button>
                    <input
                      ref={responseFileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleResponseFileUpload}
                    />
                  </div>
                </div>
                <button
                  onClick={sendQuoteResponse}
                  disabled={sendingResponse || (!newResponse.message.trim() && newResponse.attachments.length === 0)}
                  className="px-6 py-3 rounded-lg font-medium self-end"
                  style={{
                    backgroundColor: isDark ? '#00ff88' : '#10b981',
                    color: isDark ? '#000' : '#fff',
                    opacity: (sendingResponse || (!newResponse.message.trim() && newResponse.attachments.length === 0)) ? 0.5 : 1
                  }}
                >
                  {sendingResponse ? 'Sending...' : 'Send Response'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    
      {signQuotes.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: isDark ? '#333' : '#e2e8f0' }} />
          <p style={{ color: isDark ? '#666' : '#94a3b8' }}>No sign quote requests yet.</p>
        </div>
      ) : (
        signQuotes.map((quote) => (
          <div 
            key={quote.id}
            className="p-4 rounded-lg"
            style={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
              border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                  {quote.name}
                </h3>
                <p style={{ color: isDark ? '#666' : '#64748b' }}>{quote.email} - {quote.phone || 'No phone'}</p>
                {quote.company && (
                  <p style={{ color: isDark ? '#888' : '#94a3b8' }} className="text-sm">Company: {quote.company}</p>
                )}
                <p className="mt-2" style={{ color: isDark ? '#a0a0a0' : '#334155' }}>{quote.message}</p>
                
                {/* Display attachments */}
                {(quote.attachments && quote.attachments.length > 0) || quote.image_url ? (
                  <div className="mt-3">
                    <p style={{ color: isDark ? '#00ff88' : '#10b981' }} className="text-sm mb-2">
                      Attachments ({quote.attachments?.length || 1}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {quote.attachments && quote.attachments.length > 0 ? (
                        quote.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={att.url?.startsWith('http') ? att.url : `${API_URL}${att.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group"
                          >
                            {att.type?.startsWith('image/') ? (
                              <img 
                                src={att.url?.startsWith('http') ? att.url : `${API_URL}${att.url}`}
                                alt={att.name}
                                className="w-20 h-20 object-cover rounded-lg hover:opacity-80 transition-opacity"
                                onError={(e) => { 
                                  e.target.onerror = null; 
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect fill="%23374151" width="80" height="80" rx="8"/><text x="40" y="45" text-anchor="middle" fill="%239ca3af" font-size="10">No Image</text></svg>'; 
                                }}
                              />
                            ) : (
                              <div className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={{ backgroundColor: isDark ? '#333' : '#e2e8f0', color: isDark ? '#ccc' : '#334155' }}>
                                {att.name}
                              </div>
                            )}
                          </a>
                        ))
                      ) : quote.image_url ? (
                        <img 
                          src={quote.image_url?.startsWith('http') ? quote.image_url : `${API_URL}${quote.image_url}`}
                          alt="Quote attachment" 
                          className="max-w-xs rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ maxHeight: '150px', objectFit: 'cover' }}
                          onClick={() => window.open(quote.image_url?.startsWith('http') ? quote.image_url : `${API_URL}${quote.image_url}`, '_blank')}
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 100"><rect fill="%23374151" width="150" height="100" rx="8"/><text x="75" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">Image unavailable</text></svg>'; 
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : null}
                
                {/* Admin Notes Section */}
                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255, 255, 0, 0.1)' : 'rgba(234, 179, 8, 0.1)', border: `1px solid ${isDark ? '#ffff00' : '#eab308'}` }}>
                  <label className="text-xs font-semibold mb-2 block" style={{ color: isDark ? '#ffff00' : '#ca8a04' }}>
                    Admin Notes & Details
                  </label>
                  <textarea
                    value={quote.admin_notes || ''}
                    onChange={(e) => {
                      const newNotes = e.target.value;
                      setQuotes(quotes.map(q => q.id === quote.id ? { ...q, admin_notes: newNotes } : q));
                    }}
                    onBlur={(e) => saveAdminNotes(quote.id, e.target.value)}
                    placeholder="Add notes about this quote (pricing discussed, follow-up details, special requirements...)"
                    className="w-full p-2 rounded text-sm resize-none"
                    rows={2}
                    style={{
                      backgroundColor: isDark ? '#1a1a1a' : '#fff',
                      color: isDark ? '#fff' : '#0f172a',
                      border: `1px solid ${isDark ? '#444' : '#e2e8f0'}`
                    }}
                  />
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3 items-center">
                  <span 
                    className="px-2 py-1 rounded text-xs"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 0, 255, 0.2)' : 'rgba(255, 20, 147, 0.1)',
                      color: isDark ? '#ff00ff' : '#ff1493'
                    }}
                  >
                    {quote.sign_type}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    quote.status === 'new' ? 'bg-blue-500/20 text-blue-500' :
                    quote.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-500' :
                    quote.status === 'responded' ? 'bg-purple-500/20 text-purple-500' :
                    quote.status === 'customer_replied' ? 'bg-cyan-500/20 text-cyan-500' :
                    quote.status === 'final' ? 'bg-pink-500/20 text-pink-500' :
                    quote.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                    'bg-gray-500/20 text-gray-500'
                  }`}>
                    {quote.status === 'customer_replied' ? 'Customer Replied' : 
                     quote.status === 'final' ? 'Final' :
                     quote.status === 'paid' ? 'Paid' :
                     quote.status}
                  </span>
                  <span className="px-2 py-1 rounded text-xs" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                    {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : 'Unknown date'}
                  </span>
                  
                  {/* Show linked quote value if exists */}
                  {(() => {
                    const linkedQuote = generatedQuotes.find(gq => gq.quote_request_id === quote.id);
                    if (linkedQuote) {
                      return (
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: isDark ? 'rgba(255, 255, 0, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                            color: isDark ? '#ffff00' : '#ca8a04'
                          }}
                        >
                          ¥{(linkedQuote.total || 0).toLocaleString()}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Respond Button */}
                  <button
                    onClick={() => openQuoteConversation(quote)}
                    className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: isDark ? '#ff00ff' : '#d946ef',
                      color: '#fff'
                    }}
                  >
                    <MessageSquare className="w-3 h-3" /> Respond
                  </button>
                  
                  {/* Generate Quote Button */}
                  <button
                    onClick={() => onPrefillFromQuoteRequest(quote)}
                    className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: isDark ? '#ffff00' : '#eab308',
                      color: isDark ? '#000' : '#000'
                    }}
                  >
                    <FileText className="w-3 h-3" /> Generate Quote
                  </button>
                  
                  {/* Copy Magic Link */}
                  {quote.access_token && (
                    <button
                      onClick={() => copyMagicLink(quote)}
                      className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: isDark ? '#00ffff' : '#0ea5e9',
                        color: isDark ? '#000' : '#fff'
                      }}
                    >
                      <Link className="w-3 h-3" /> Copy Link
                    </button>
                  )}
                  
                  {/* Contact Now Button */}
                  <a
                    href={`mailto:${quote.email}?subject=Re: Your Quote Request for ${quote.sign_type || 'Custom Sign'}&body=Hi ${quote.name},%0D%0A%0D%0AThank you for your interest in our signs! I'm following up on your quote request.%0D%0A%0D%0AView your quote and our response here:%0D%0A${window.location.origin}/quote/${quote.access_token || ''}%0D%0A%0D%0ABest regards,%0D%0AOkinawa Sign Studio`}
                    className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: isDark ? '#00ff88' : '#10b981',
                      color: isDark ? '#000' : '#fff'
                    }}
                    onClick={() => updateQuoteStatus(quote.id, 'contacted')}
                  >
                    <Mail className="w-3 h-3" /> Email
                  </a>
                  {quote.phone && (
                    <a
                      href={`tel:${quote.phone}`}
                      className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: isDark ? '#00ffff' : '#0ea5e9',
                        color: isDark ? '#000' : '#fff'
                      }}
                    >
                      <Phone className="w-3 h-3" /> Call
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <select
                  value={quote.status}
                  onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                  className="px-3 py-2 rounded text-sm"
                  style={{
                    backgroundColor: isDark ? '#222' : '#f1f5f9',
                    color: isDark ? '#ffffff' : '#0f172a',
                    border: 'none'
                  }}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="responded">Responded</option>
                  <option value="customer_replied">Customer Replied</option>
                  <option value="final">Final</option>
                  <option value="paid">Paid</option>
                  <option value="completed">Completed</option>
                </select>
                <button
                  onClick={() => deleteQuote(quote.id)}
                  className="p-2 rounded hover:bg-red-500/20 text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default SignQuotesTab;
